// src/services/weatherDataService.js
// Historical weather data service — AfriClimate API
// (api.wirelessplanet.co.ke/api/v1)

import { AFRICLIMATE_API as TTN, apiHeaders as ttnHeaders } from './config';

// ─── AfriClimate field mapper (same as ttn/readings.js mapReading) ───────────
const mapReading = (r) => ({
  timestamp:   r.receivedAt,
  temperature: r.sensorData?.airTemperature      ?? null,
  humidity:    r.sensorData?.airHumidity          ?? null,
  pressure:    r.sensorData?.barometricPressure != null
                 ? r.sensorData.barometricPressure / 100
                 : null,
  wind_speed:  r.sensorData?.windSpeed            ?? null,
  wind_dir:    r.sensorData?.windDirectionSensor  ?? null,
  peak_gust:   r.sensorData?.peakWindGust         ?? null,
  rain_gauge:  r.sensorData?.rainGauge            ?? null,
  rain_accum:  r.sensorData?.rainAccumulation     ?? null,
  uv_index:    r.sensorData?.uvIndex              ?? null,
  light:       r.sensorData?.lightIntensity       ?? null,
  rssi:        r.gateway?.rssi   ?? null,
  snr:         r.gateway?.snr    ?? null,
  // chart-ready aliases
  windSpeed:   r.sensorData?.windSpeed   ?? 0,
  rainfall:    r.sensorData?.rainGauge   ?? 0,
  uvIndex:     r.sensorData?.uvIndex     ?? 0,
});

// ─── Fetch weather data with filters ─────────────────────────────────────────

export const fetchWeatherData = async ({
  stationId    = null,
  fromDateTime = null,
  toDateTime   = null,
  page         = 1,
  pageSize     = 100,
}) => {
  const p = new URLSearchParams({ page, limit: pageSize });
  if (stationId)    p.set('deviceId', stationId);
  if (fromDateTime) p.set('startDate', new Date(fromDateTime).toISOString());
  if (toDateTime)   p.set('endDate',   new Date(toDateTime).toISOString());

  const url = `${TTN.BASE_URL}/readings?${p}`;
  console.log('Fetching weather data from:', url);

  const res = await fetch(url, { headers: ttnHeaders });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const json = await res.json();
  return {
    data:     (json.data || []).map(mapReading),
    total:    json.total    ?? null,
    has_next: json.hasNext  ?? false,
    page:     json.page     ?? page,
  };
};

// Fetch readings for a specific device ID
export const fetchWeatherDataByDeviceId = async (deviceId, page = 1, pageSize = 100) => {
  const p = new URLSearchParams({ page, limit: pageSize });
  const url = `${TTN.BASE_URL}/readings/device/${deviceId}?${p}`;
  console.log('Fetching weather data by device ID:', url);

  const res = await fetch(url, { headers: ttnHeaders });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const json = await res.json();
  return {
    data:     (json.data || []).map(mapReading),
    total:    json.total    ?? null,
    has_next: json.hasNext  ?? false,
  };
};

// ─── Statistics ───────────────────────────────────────────────────────────────

export const calculateStatistics = (weatherData) => {
  if (!weatherData || weatherData.length === 0) return null;

  const fieldStats = (field) => {
    const values = weatherData.map(d => d[field]).filter(v => v != null && !isNaN(v));
    if (values.length === 0) return { min: 0, max: 0, average: 0, current: 0 };
    const sorted = [...values].sort((a, b) => a - b);
    return {
      min:     Math.min(...values),
      max:     Math.max(...values),
      average: values.reduce((s, v) => s + v, 0) / values.length,
      current: weatherData[weatherData.length - 1]?.[field] || 0,
      median:  sorted[Math.floor(sorted.length / 2)],
    };
  };

  return {
    temperature:    fieldStats('temperature'),
    humidity:       fieldStats('humidity'),
    pressure:       fieldStats('pressure'),
    windSpeed:      fieldStats('wind_speed'),
    rainfall: {
      total:             weatherData.reduce((s, d) => s + (d.rain_gauge || 0), 0),
      totalAccumulation: weatherData[weatherData.length - 1]?.rain_accum || 0,
      average:           fieldStats('rain_gauge').average,
      min:               0,
      max:               Math.max(...weatherData.map(d => d.rain_gauge || 0)),
      current:           weatherData[weatherData.length - 1]?.rain_gauge || 0,
    },
    uvIndex:        fieldStats('uv_index'),
    lightIntensity: fieldStats('light'),
    dataPoints:     weatherData.length,
  };
};

// ─── Date helpers ─────────────────────────────────────────────────────────────

export const formatDateForAPI = (date) => date ? new Date(date).toISOString() : null;

export const getDateRanges = () => {
  const now   = new Date();
  const today = new Date(now); today.setHours(0, 0, 0, 0);
  const yest  = new Date(today); yest.setDate(yest.getDate() - 1);
  const week  = new Date(today); week.setDate(week.getDate() - 7);
  const month = new Date(today); month.setMonth(month.getMonth() - 1);
  const year  = new Date(today); year.setFullYear(year.getFullYear() - 1);
  return {
    today:     { from: today, to: now },
    yesterday: { from: yest, to: new Date(yest.getTime() + 86399999) },
    lastWeek:  { from: week,  to: now },
    lastMonth: { from: month, to: now },
    lastYear:  { from: year,  to: now },
  };
};

// ─── Aggregation ──────────────────────────────────────────────────────────────

const NUMERIC_FIELDS = ['temperature', 'humidity', 'pressure', 'wind_speed',
                        'rain_gauge', 'uv_index', 'light', 'wind_dir',
                        'rain_accum', 'peak_gust'];

const avgField = (items, field) => {
  const vals = items.map(i => i[field]).filter(v => v != null && !isNaN(v));
  return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
};

export const aggregateDataByHour = (data, numHours = 24) => {
  if (!data || data.length === 0) return [];

  const groups = {};
  data.forEach(item => {
    const d = new Date(item.timestamp);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}-${d.getHours()}`;
    if (!groups[key]) groups[key] = {
      ts: new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours()),
      items: [],
    };
    groups[key].items.push(item);
  });

  // Produce exactly numHours slots ending at the current hour
  const now = new Date(); now.setMinutes(0, 0, 0);
  const slots = [];
  for (let i = numHours - 1; i >= 0; i--) {
    const slotTs = new Date(now.getTime() - i * 3600000);
    const key = `${slotTs.getFullYear()}-${slotTs.getMonth()}-${slotTs.getDate()}-${slotTs.getHours()}`;
    const group = groups[key];
    if (group && group.items.length > 0) {
      const avg = {};
      NUMERIC_FIELDS.forEach(f => { avg[f] = avgField(group.items, f); });
      slots.push({
        timestamp: slotTs,
        time: slotTs.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        ...avg,
        windSpeed: avg.wind_speed,
        rainfall:  avg.rain_gauge,
        uvIndex:   avg.uv_index,
      });
    } else {
      const nullAvg = {};
      NUMERIC_FIELDS.forEach(f => { nullAvg[f] = null; });
      slots.push({
        timestamp: slotTs,
        time: slotTs.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        ...nullAvg,
        windSpeed: null,
        rainfall:  null,
        uvIndex:   null,
      });
    }
  }
  return slots;
};

export const aggregateDataByDay = (data, numDays = 7) => {
  if (!data || data.length === 0) return [];

  // Build a group map from raw data
  const groups = {};
  data.forEach(item => {
    const d = new Date(item.timestamp);
    const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    if (!groups[key]) groups[key] = {
      ts: new Date(d.getFullYear(), d.getMonth(), d.getDate()),
      items: [],
    };
    groups[key].items.push(item);
  });

  // Produce exactly numDays slots, newest last
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const slots = [];
  for (let i = numDays - 1; i >= 0; i--) {
    const slotDate = new Date(today); slotDate.setDate(slotDate.getDate() - i);
    const key = `${slotDate.getFullYear()}-${String(slotDate.getMonth()).padStart(2,'0')}-${String(slotDate.getDate()).padStart(2,'0')}`;
    const group = groups[key];
    if (group && group.items.length > 0) {
      const avg = {};
      NUMERIC_FIELDS.forEach(f => { avg[f] = avgField(group.items, f); });
      slots.push({
        timestamp: slotDate,
        time: slotDate.toLocaleDateString([], { month: 'short', day: 'numeric' }),
        ...avg,
        windSpeed: avg.wind_speed,
        rainfall:  avg.rain_gauge,
        uvIndex:   avg.uv_index,
      });
    } else {
      // Day has no data — include slot with nulls so chart shows the gap
      const nullAvg = {};
      NUMERIC_FIELDS.forEach(f => { nullAvg[f] = null; });
      slots.push({
        timestamp: slotDate,
        time: slotDate.toLocaleDateString([], { month: 'short', day: 'numeric' }),
        ...nullAvg,
        windSpeed: null,
        rainfall:  null,
        uvIndex:   null,
      });
    }
  }
  return slots;
};

export const filterLastHours = (data, hours = 1) => {
  if (!data || data.length === 0) return [];
  const cutoff = Date.now() - hours * 3600000;
  return data
    .filter(item => new Date(item.timestamp) >= cutoff)
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
    .map(item => ({
      ...item,
      time:      new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      windSpeed: item.wind_speed ?? item.windSpeed ?? 0,
      rainfall:  item.rain_gauge ?? item.rainfall ?? 0,
      uvIndex:   item.uv_index   ?? item.uvIndex  ?? 0,
    }));
};

export const filterLastDays = (data, days = 7) => {
  if (!data || data.length === 0) return [];
  const cutoff = Date.now() - days * 86400000;
  return data.filter(item => new Date(item.timestamp) >= cutoff);
};

const weatherDataService = {
  fetchWeatherData,
  fetchWeatherDataByDeviceId,
  calculateStatistics,
  formatDateForAPI,
  getDateRanges,
  aggregateDataByHour,
  aggregateDataByDay,
  filterLastHours,
  filterLastDays,
};
export default weatherDataService;
